import React, { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchUserProfile, updateUserProfile, logoutUser } from '../store/slices/userSlice';
import api from '../api';
import AlertModal from '../components/AlertModal';
import ReactSelect, { OptionType } from '../components/ui/ReactSelect';
import { storage, invoiceProfile } from '../utils/helpers';

const AccountSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, isLoading, error: reduxError } = useAppSelector((state) => state.user);

  // Convert ApiUser to local form state format
  const getUserInfoFromRedux = useMemo(() => {
    const profile = invoiceProfile.get();
    if (!user) {
      return {
        name: '',
        nickname: '',
        email: '',
        phone: '',
        address: profile.senderAddress || '',
        language: 'English',
        profilePicture: '',
        payeeName: profile.payeeName || '',
        bankName: profile.bankName || '',
        accountNumber: profile.accountNumber || '',
        ifscCode: profile.ifscCode || '',
        notifications: {
          email: true,
          push: true,
          sms: false
        }
      };
    }
    return {
      name: user.name || '',
      nickname: user.nickname || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || profile.senderAddress || '',
      language: user.language || 'English',
      profilePicture: user.avatarUrl || '',
      payeeName: user.bankDetails?.payeeName || profile.payeeName || '',
      bankName: user.bankDetails?.bankName || profile.bankName || '',
      accountNumber: user.bankDetails?.accountNumber || profile.accountNumber || '',
      ifscCode: user.bankDetails?.ifscCode || profile.ifscCode || '',
      notifications: {
        email: user.isEmailNotification ?? true,
        push: user.isPushNotifications ?? true,
        sms: user.isSmsNotifications ?? false,
      },
    };
  }, [user]);

  const initialUserInfo = getUserInfoFromRedux;
  const [userInfo, setUserInfo] = useState(initialUserInfo);
  const [savedUserInfo, setSavedUserInfo] = useState(initialUserInfo);
  const [error, setError] = useState<string | null>(null);
  const [bankErrors, setBankErrors] = useState<{ payeeName?: string, bankName?: string, accountNumber?: string, ifscCode?: string }>({});

  const [alertModal, setAlertModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    title: '',
    message: '',
    type: 'info',
  });

  // Fetch user data from Redux on component mount
  useEffect(() => {
    if (!user) {
      dispatch(fetchUserProfile());
    }
  }, [dispatch, user]);

  // Update local state when Redux user data changes
  useEffect(() => {
    if (user) {
      const userInfoData = getUserInfoFromRedux;
      setUserInfo(userInfoData);
      setSavedUserInfo(userInfoData);
      setProfileImageError(false);
      // Sync to localStorage for invoice pre-fill
      invoiceProfile.set({
        senderName: user.name,
        senderAddress: user.address,
        senderEmail: user.email,
        payeeName: user.bankDetails?.payeeName,
        bankName: user.bankDetails?.bankName,
        accountNumber: user.bankDetails?.accountNumber,
        ifscCode: user.bankDetails?.ifscCode,
      });
    }
  }, [user]);

  // Sync error from Redux
  useEffect(() => {
    if (reduxError) {
      setError(reduxError);
    }
  }, [reduxError]);

  const [isEditing, setIsEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Prevent background scroll when modals are open
  useEffect(() => {
    if (showChangePassword || showDeleteConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showChangePassword, showDeleteConfirm]);

  // Get display values: use savedUserInfo in view mode, userInfo in edit mode
  // If no savedUserInfo, use Redux user data
  const displayUserInfo = isEditing ? userInfo : (savedUserInfo.name || savedUserInfo.email ? savedUserInfo : getUserInfoFromRedux);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState<{ [key: string]: string }>({});

  const handleBankDetailChange = (field: 'payeeName' | 'bankName' | 'accountNumber' | 'ifscCode', value: string) => {
    const newUserInfo = { ...userInfo, [field]: value };
    setUserInfo(newUserInfo);

    const bankDetailsFilled = [newUserInfo.payeeName, newUserInfo.bankName, newUserInfo.accountNumber, newUserInfo.ifscCode].filter(val => val && val.trim() !== '').length;

    if (bankDetailsFilled === 0) {
      setBankErrors({});
    } else if (bankErrors[field]) {
      setBankErrors({ ...bankErrors, [field]: undefined });
    }
  };

  const handleSave = async () => {
    try {
      setError(null);

      // Validate bank details
      const bankDetailsFilled = [userInfo.payeeName, userInfo.bankName, userInfo.accountNumber, userInfo.ifscCode].filter(val => val && val.trim() !== '').length;

      let newBankErrors: any = {};

      if (bankDetailsFilled > 0 && bankDetailsFilled < 4) {
        if (!userInfo.payeeName || userInfo.payeeName.trim() === '') newBankErrors.payeeName = 'Required if entering bank details';
        if (!userInfo.bankName || userInfo.bankName.trim() === '') newBankErrors.bankName = 'Required if entering bank details';
        if (!userInfo.accountNumber || userInfo.accountNumber.trim() === '') newBankErrors.accountNumber = 'Required if entering bank details';
        if (!userInfo.ifscCode || userInfo.ifscCode.trim() === '') newBankErrors.ifscCode = 'Required if entering bank details';

        setBankErrors(newBankErrors);

        // Scroll to the first error after React updates the DOM
        setTimeout(() => {
          const firstErrorField = document.querySelector('.bank-error-field');
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 0);

        return;
      }

      setBankErrors({});

      // Prepare update data
      const updateData = {
        name: userInfo.name,
        nickname: userInfo.nickname,
        email: userInfo.email,
        phone: userInfo.phone,
        address: userInfo.address,
        language: userInfo.language,
        isEmailNotification: userInfo.notifications.email,
        isPushNotifications: userInfo.notifications.push,
        isSmsNotifications: userInfo.notifications.sms,
        ...(bankDetailsFilled === 4 ? {
          bankDetails: {
            payeeName: userInfo.payeeName,
            bankName: userInfo.bankName,
            accountNumber: userInfo.accountNumber,
            ifscCode: userInfo.ifscCode,
          }
        } : (bankDetailsFilled === 0 ? { bankDetails: null } : {})),
      };

      // Dispatch update profile action
      const result = await dispatch(updateUserProfile(updateData)).unwrap();

      // Update local state with response and persist to localStorage for invoice pre-fill
      const updatedUserInfo = {
        name: result.name || '',
        nickname: result.nickname || '',
        email: result.email || '',
        phone: result.phone || '',
        address: result.address || userInfo.address || '',
        language: result.language || 'English',
        profilePicture: result.avatarUrl || userInfo.profilePicture,
        payeeName: result.bankDetails?.payeeName || userInfo.payeeName || '',
        bankName: result.bankDetails?.bankName || userInfo.bankName || '',
        accountNumber: result.bankDetails?.accountNumber || userInfo.accountNumber || '',
        ifscCode: result.bankDetails?.ifscCode || userInfo.ifscCode || '',
        notifications: {
          email: result.isEmailNotification ?? true,
          push: result.isPushNotifications ?? true,
          sms: result.isSmsNotifications ?? false,
        },
      };
      invoiceProfile.set({
        senderName: updatedUserInfo.name,
        senderAddress: updatedUserInfo.address,
        senderEmail: updatedUserInfo.email,
        payeeName: updatedUserInfo.payeeName,
        bankName: updatedUserInfo.bankName,
        accountNumber: updatedUserInfo.accountNumber,
        ifscCode: updatedUserInfo.ifscCode,
      });
      setUserInfo(updatedUserInfo);
      setSavedUserInfo(updatedUserInfo); // Update saved state with new values
      setIsEditing(false);
      // Show success message
      setAlertModal({
        open: true,
        title: 'Success',
        message: 'Settings saved successfully!',
        type: 'success',
      });
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err || 'Failed to update profile. Please try again.');
    }
  };



  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Clear general error message when user starts typing
    if (error) {
      setError(null);
    }
  };

  const validatePasswordForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      setError(null);
      setIsChangingPassword(true);

      // Call change password API
      await api.auth.changePassword({
        oldPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      // Show success message
      setAlertModal({
        open: true,
        title: 'Success',
        message: 'Password changed successfully!',
        type: 'success',
      });

      // Close modal and reset form
      setShowChangePassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
      setError(null); // Clear any previous errors
    } catch (err: any) {
      console.error('Error changing password:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to change password. Please try again.';
      setError(errorMessage);

      // Set field-specific errors based on error message
      const lowerErrorMessage = errorMessage.toLowerCase();
      setPasswordErrors({
        currentPassword: lowerErrorMessage.includes('current') || lowerErrorMessage.includes('old') || lowerErrorMessage.includes('incorrect') || lowerErrorMessage.includes('wrong') ? errorMessage : '',
        newPassword: lowerErrorMessage.includes('new') && !lowerErrorMessage.includes('current') ? errorMessage : '',
        confirmPassword: '',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      await api.user.deleteMe();
      await dispatch(logoutUser());
    } catch (err: any) {
      console.error('Error deleting account:', err);
      setAlertModal({
        open: true,
        title: 'Error',
        message: err.message || 'Failed to delete account.',
        type: 'error',
      });
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Helper function to compress image
  const compressImage = (file: File, maxWidth: number = 800, maxHeight: number = 800, quality: number = 0.8): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with quality compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // Check if compressed size is acceptable (2MB)
              const maxSize = 2 * 1024 * 1024; // 2MB
              if (blob.size > maxSize) {
                // Try with lower quality if still too large
                if (quality > 0.3) {
                  compressImage(file, maxWidth, maxHeight, quality - 0.1)
                    .then(resolve)
                    .catch(reject);
                } else {
                  reject(new Error('Image is too large even after compression. Please use a smaller image.'));
                }
                return;
              }

              // Convert blob to data URL
              const reader = new FileReader();
              reader.onload = (e) => {
                resolve(e.target?.result as string);
              };
              reader.onerror = () => reject(new Error('Failed to read compressed image'));
              reader.readAsDataURL(blob);
            },
            file.type || 'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAlertModal({
        open: true,
        title: 'Error',
        message: 'Please select a valid image file.',
        type: 'error',
      });
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      setAlertModal({
        open: true,
        title: 'Error',
        message: 'Image size must be less than or equal to 2MB.',
        type: 'error',
      });
      return;
    }

    try {
      setError(null);

      // Compress and convert image to data URL
      const dataUrl = await compressImage(file);

      try {
        // Update profile with the compressed data URL as avatarUrl
        const result = await dispatch(updateUserProfile({ avatarUrl: dataUrl })).unwrap();

        // Update local state
        const updatedUserInfo = {
          ...userInfo,
          profilePicture: result.avatarUrl || dataUrl,
        };
        setUserInfo(updatedUserInfo);
        setSavedUserInfo(updatedUserInfo);
        setProfileImageError(false); // Reset image error state

        // Show success message
        setAlertModal({
          open: true,
          title: 'Success',
          message: 'Profile picture updated successfully!',
          type: 'success',
        });
      } catch (err: any) {
        console.error('Error updating profile picture:', err);
        setError(err || 'Failed to update profile picture. Please try again.');
        setAlertModal({
          open: true,
          title: 'Error',
          message: err || 'Failed to update profile picture. Please try again.',
          type: 'error',
        });
      }
    } catch (err: any) {
      console.error('Error processing profile picture:', err);
      setAlertModal({
        open: true,
        title: 'Error',
        message: err.message || 'Failed to process image. Please try again.',
        type: 'error',
      });
    }

    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-900 text-lg">Loading account settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0] relative overflow-hidden">
      {/* Background gradient shapes */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl"></div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 relative z-10">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-orange-200/50 shadow-lg">
          {error && isEditing && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Account Settings</h2>
            {!isEditing ? (
              <button
                onClick={() => {
                  // When entering edit mode, ensure userInfo matches savedUserInfo
                  setUserInfo(savedUserInfo);
                  setIsEditing(true);
                }}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-orange-200"
              >
                Edit Profile
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
                <button
                  onClick={() => {
                    // When canceling, revert userInfo to savedUserInfo
                    setUserInfo(savedUserInfo);
                    setIsEditing(false);
                    setError(null); // Clear errors when canceling
                    setBankErrors({}); // Clear bank errors
                  }}
                  className="w-full sm:w-auto px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>

          {/* Profile Picture Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Profile Picture</h3>
            <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 text-center sm:text-left">
              <div className="relative">
                {displayUserInfo.profilePicture && !profileImageError ? (
                  <div className="w-24 h-24 rounded-full p-0.5 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600">
                    <img
                      src={displayUserInfo.profilePicture}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover"
                      onError={() => setProfileImageError(true)}
                    />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full p-0.5 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 shadow-lg">
                    <div className="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-3xl">
                        {displayUserInfo.name?.charAt(0)?.toUpperCase() || displayUserInfo.email?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => document.getElementById('profile-picture-input')?.click()}
                  className="absolute -bottom-2 -right-2 w-10 h-10 bg-white border border-orange-200 rounded-full flex items-center justify-center text-orange-600 hover:bg-orange-50 hover:border-orange-300 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-110"
                  aria-label="Update profile picture"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
                <input
                  id="profile-picture-input"
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                  className="hidden"
                />
              </div>
              <div>
                <h4 className="text-gray-900 font-semibold mb-2">Update Profile Picture</h4>
                <p className="text-gray-600 text-sm mb-3">Click the camera icon to upload a new profile picture</p>
              </div>
            </div>
          </div>

          {/* Profile Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={userInfo.name}
                    onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none"
                  />
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.name || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nickname</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={userInfo.nickname}
                    onChange={(e) => setUserInfo({ ...userInfo, nickname: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none"
                    placeholder="Enter your nickname"
                  />
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.nickname || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                {isEditing ? (
                  <input
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none"
                  />
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.email || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={userInfo.phone}
                    onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none"
                  />
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.phone || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                {isEditing ? (
                  <textarea
                    value={userInfo.address}
                    onChange={(e) => setUserInfo({ ...userInfo, address: e.target.value })}
                    rows={3}
                    placeholder="Street, city, postal code, country"
                    className="w-full px-4 py-3 bg-white border border-orange-200/50 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none resize-none"
                  />
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.address || <span className="text-gray-400">—</span>}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Used for invoices and documents</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Language</label>
                {isEditing ? (
                  <ReactSelect
                    options={[
                      { value: 'English', label: 'English' },
                      { value: 'Spanish', label: 'Spanish' },
                      { value: 'French', label: 'French' },
                      { value: 'German', label: 'German' },
                    ]}
                    value={userInfo.language ? { value: userInfo.language, label: userInfo.language } : null}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setUserInfo({ ...userInfo, language: selectedOption.value });
                      }
                    }}
                    placeholder="Select language..."
                  />
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.language || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>

              {/* Notification Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h3>
                <div className="space-y-4">
                  {/* Email Notifications */}
                  {isEditing ? (
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={userInfo.notifications.email}
                          onChange={(e) => setUserInfo({
                            ...userInfo,
                            notifications: { ...userInfo.notifications, email: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-gray-100 peer-checked:bg-green-500 peer-checked:border-green-500 transition-all duration-200 flex items-center justify-center shadow-sm peer-checked:shadow-green-500/50 group-hover:border-gray-400 peer-checked:group-hover:border-green-400">
                          {userInfo.notifications.email && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-gray-900 group-hover:text-gray-700 transition-colors">Email Notifications</span>
                    </label>
                  ) : (
                    <div className="flex items-center space-x-3">
                      {displayUserInfo.notifications.email ? (
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-gray-100 flex items-center justify-center">
                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      )}
                      <span className="text-gray-700">Email Notifications</span>
                    </div>
                  )}

                  {/* Push Notifications */}
                  {isEditing ? (
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={userInfo.notifications.push}
                          onChange={(e) => setUserInfo({
                            ...userInfo,
                            notifications: { ...userInfo.notifications, push: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-gray-100 peer-checked:bg-green-500 peer-checked:border-green-500 transition-all duration-200 flex items-center justify-center shadow-sm peer-checked:shadow-green-500/50 group-hover:border-gray-400 peer-checked:group-hover:border-green-400">
                          {userInfo.notifications.push && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-gray-900 group-hover:text-gray-700 transition-colors">Push Notifications</span>
                    </label>
                  ) : (
                    <div className="flex items-center space-x-3">
                      {displayUserInfo.notifications.push ? (
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-gray-100 flex items-center justify-center">
                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      )}
                      <span className="text-gray-700">Push Notifications</span>
                    </div>
                  )}

                  {/* SMS Notifications */}
                  {/* {isEditing ? (
                    <label className="flex items-center space-x-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={userInfo.notifications.sms}
                          onChange={(e) => setUserInfo({
                            ...userInfo,
                            notifications: { ...userInfo.notifications, sms: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-6 h-6 rounded-full border-2 border-gray-300 bg-gray-100 peer-checked:bg-green-500 peer-checked:border-green-500 transition-all duration-200 flex items-center justify-center shadow-sm peer-checked:shadow-green-500/50 group-hover:border-gray-400 peer-checked:group-hover:border-green-400">
                          {userInfo.notifications.sms && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-gray-900 group-hover:text-gray-700 transition-colors">SMS Notifications</span>
                    </label>
                  ) : (
                    <div className="flex items-center space-x-3">
                      {displayUserInfo.notifications.sms ? (
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-gray-100 flex items-center justify-center">
                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      )}
                      <span className="text-gray-700">SMS Notifications</span>
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          </div>

          {/* Bank Details Section - For invoices */}
          <div className="mt-12 pt-8 border-t border-orange-200/50">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Bank details</h3>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setUserInfo({
                      ...userInfo,
                      payeeName: '',
                      bankName: '',
                      accountNumber: '',
                      ifscCode: '',
                    });
                    setBankErrors({});
                  }}
                  className="text-sm px-3 py-1.5 text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 rounded-lg font-medium transition-colors border border-red-200/50 hover:border-red-300 flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Reset Details
                </button>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-6 -mt-2">Used to pre-fill bank information on your invoices</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Your bank account</label>
                {isEditing ? (
                  <div className={bankErrors.payeeName ? "bank-error-field" : ""}>
                    <input
                      type="text"
                      value={userInfo.payeeName}
                      onChange={(e) => handleBankDetailChange('payeeName', e.target.value)}
                      placeholder="Name as on bank account"
                      className={`w-full px-4 py-3 bg-white border ${bankErrors.payeeName ? 'border-red-500' : 'border-orange-200/50'} rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none`}
                    />
                    {bankErrors.payeeName && <p className="text-red-500 text-sm mt-1">{bankErrors.payeeName}</p>}
                  </div>
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.payeeName || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Name</label>
                {isEditing ? (
                  <div className={bankErrors.bankName ? "bank-error-field" : ""}>
                    <input
                      type="text"
                      value={userInfo.bankName}
                      onChange={(e) => handleBankDetailChange('bankName', e.target.value)}
                      placeholder="e.g. HDFC Bank"
                      className={`w-full px-4 py-3 bg-white border ${bankErrors.bankName ? 'border-red-500' : 'border-orange-200/50'} rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none`}
                    />
                    {bankErrors.bankName && <p className="text-red-500 text-sm mt-1">{bankErrors.bankName}</p>}
                  </div>
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.bankName || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Account Number</label>
                {isEditing ? (
                  <div className={bankErrors.accountNumber ? "bank-error-field" : ""}>
                    <input
                      type="text"
                      value={userInfo.accountNumber}
                      onChange={(e) => handleBankDetailChange('accountNumber', e.target.value)}
                      placeholder="e.g. 50100296774501"
                      className={`w-full px-4 py-3 bg-white border ${bankErrors.accountNumber ? 'border-red-500' : 'border-orange-200/50'} rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none`}
                    />
                    {bankErrors.accountNumber && <p className="text-red-500 text-sm mt-1">{bankErrors.accountNumber}</p>}
                  </div>
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.accountNumber || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">IFSC Code</label>
                {isEditing ? (
                  <div className={bankErrors.ifscCode ? "bank-error-field" : ""}>
                    <input
                      type="text"
                      value={userInfo.ifscCode}
                      onChange={(e) => handleBankDetailChange('ifscCode', e.target.value)}
                      placeholder="e.g. HDFC0000520"
                      className={`w-full px-4 py-3 bg-white border ${bankErrors.ifscCode ? 'border-red-500' : 'border-orange-200/50'} rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none`}
                    />
                    {bankErrors.ifscCode && <p className="text-red-500 text-sm mt-1">{bankErrors.ifscCode}</p>}
                  </div>
                ) : (
                  <div className="px-4 py-3 min-h-[48px] bg-gray-50 border border-orange-200/50 rounded-xl text-gray-900 flex items-center">
                    {displayUserInfo.ifscCode || <span className="text-gray-400">—</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Security Section - Only show in view mode */}
          {!isEditing && (
            <div className="mt-12 pt-8 border-t border-orange-200/50">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Security</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="p-4 bg-gray-50 rounded-xl border border-orange-200/50 hover:border-orange-400/70 hover:bg-orange-50/30 transition-colors text-left"
                >
                  <h4 className="text-gray-900 font-semibold mb-2">Change Password</h4>
                  <p className="text-gray-600 text-sm">Update your account password</p>
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-4 bg-red-50 rounded-xl border border-red-200 hover:border-red-400 hover:bg-red-100 transition-colors text-left"
                >
                  <h4 className="text-red-700 font-semibold mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Account
                  </h4>
                  <p className="text-red-600/80 text-sm">Delete your account and all data</p>
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 max-w-md w-full border border-orange-200/50 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Change Password</h3>
              <button
                onClick={() => {
                  setShowChangePassword(false);
                  setError(null); // Clear error when closing modal
                  setPasswordErrors({}); // Clear password errors
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' }); // Reset form
                }}
                className="text-gray-400 hover:text-gray-900 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Error message display */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className={`w-full px-4 py-3 bg-white border rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none transition-all ${passwordErrors.currentPassword ? 'border-red-500' : 'border-orange-200/50'
                    }`}
                  placeholder="Enter your current password"
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-sm text-red-700">{passwordErrors.currentPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className={`w-full px-4 py-3 bg-white border rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none transition-all ${passwordErrors.newPassword ? 'border-red-500' : 'border-orange-200/50'
                    }`}
                  placeholder="Enter your new password"
                />
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-sm text-red-700">{passwordErrors.newPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className={`w-full px-4 py-3 bg-white border rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400/70 focus:outline-none transition-all ${passwordErrors.confirmPassword ? 'border-red-500' : 'border-orange-200/50'
                    }`}
                  placeholder="Confirm your new password"
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-700">{passwordErrors.confirmPassword}</p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowChangePassword(false)}
                  disabled={isChangingPassword}
                  className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        open={alertModal.open}
        onClose={() => setAlertModal({ ...alertModal, open: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 max-w-md w-full border border-red-200/50 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-red-600 flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Delete Account
              </h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-900 text-2xl font-bold"
                disabled={isDeleting}
              >
                ×
              </button>
            </div>

            <div className="mb-8">
              <p className="text-gray-700 text-base mb-4 font-medium">Are you sure you want to delete your account?</p>
              <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-sm text-red-800">
                <p>After 30 days your account will be permanently deleted, you can recover your account within the time by login in again.</p>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-medium transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex justify-center items-center"
              >
                {isDeleting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Yes, Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
